import { useCallback, useEffect, useRef, useState } from 'react';
import { Avatar, AvatarFallback } from '@/components/shadcn-ui/avatar';
import { Loader2 } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { appAxios } from '@/network/base/appAxios';
import { toast } from 'sonner';


export function AdminChatHistory({ chatHistory: propMessages, isDirectLoad = false }) {
  const bottomRef = useRef(null);
  const { userId, permitChatId } = useParams();

  const [fetchedMessages, setFetchedMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchChatHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await appAxios.get('/Admin/v1.0/chat-history-by-chat-id', {
        params: { userId, permitChatId },
      });
      setFetchedMessages(response.data?.data ?? []);
    } catch (err) {
      toast.error('Failed to fetch chat history', {
        description: err.response?.data?.message || err.message || 'An error occurred',
      });
    } finally {
      setLoading(false);
    }
  }, [userId, permitChatId]);

  useEffect(() => {
    if (isDirectLoad && userId && permitChatId) {
      fetchChatHistory();
    }
  }, [isDirectLoad, userId, permitChatId, fetchChatHistory]);

  const messages = isDirectLoad ? fetchedMessages : (propMessages ?? []);
  const sortedMessages = [...messages].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [sortedMessages.length]);

  const formatTimestamp = timestamp =>
    new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const renderBoldText = text => {
    if (!text) return null;
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('*') && part.endsWith('*')) {
        return <em key={i}>{part.slice(1, -1)}</em>;
      }
      return part;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px] gap-2 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Loading chat history…</span>
      </div>
    );
  }

  return (
    <>
      {isDirectLoad && (
        <div className="flex items-center justify-between my-4 border-b border-gray-200 pb-4">
          <h1 className="text-2xl font-bold tracking-tight font-mono">
            Chat History
          </h1>
        </div>
      )}
      <div className={`${isDirectLoad ? 'rounded-lg border p-4  overflow-y-auto !no-scrollbar' : ''}`}>
        <div className="space-y-4 pb-4">
          {sortedMessages.length === 0 ? (
            <div className="flex items-center justify-center min-h-[200px] text-gray-400 text-sm">
              No chat history available
            </div>
          ) : (
            <>
              {sortedMessages.map(message => (
                <div key={message._id} className="flex flex-col gap-2">
                  <div className={`flex gap-3 ${message.sender === 'user' ? 'flex-row' : 'flex-row-reverse'}`}>
                    {message.sender === 'ai' ? (
                      <Avatar className="size-10 bg-black flex-shrink-0">
                        <AvatarFallback>
                          <img src="/kelly.png" alt="AI Assistant" className="size-10 object-cover bg-black" />
                        </AvatarFallback>
                      </Avatar>
                    ) : (
                      <Avatar className="dark size-10 bg-background-secondary flex-shrink-0">
                        <AvatarFallback className="dark bg-background-secondary text-white text-sm font-medium">
                          U
                        </AvatarFallback>
                      </Avatar>
                    )}

                    <div className="flex flex-col gap-1 max-w-[80%]">
                      <div
                        className={`rounded-lg p-3 ${message.sender === 'user' ? 'dark bg-secondary/90 text-white' : 'text-white bg-neutral-800'
                          }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{renderBoldText(message.message)}</p>

                        {message.done && message.metadata && Object.keys(message.metadata).length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-600">
                            <p className="flex items-center gap-2 text-xs text-gray-300 hover:text-white transition-colors">
                              <span>✓ Form Completed</span>
                            </p>
                          </div>
                        )}
                      </div>

                      <span className={`text-xs text-gray-400 ${message.sender === 'user' ? 'text-left' : 'text-right'}`}>
                        {formatTimestamp(message.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </>
          )}
        </div>
      </div>
    </>
  );
}
